<template>
  <div id="appViewer">
    <MenuView
        id="menu"
        ref="menuView"
        :logged-user="loggedUser"
        :main-title="mainTitle"
        :sub-title="subTitle"
        :sub-title-icon="subTitleIcon"
        :sub-title-is-editable="subTitleIsEditable"
        :display-sub-title-in-green="displaySubTitleInGreen"
        :is-logged-view="isLoggedView"
        @log-out="logout"
        @open-profile="openProfile"
    />
    <VerticalMenus
        v-show="isLoggedView"
        ref="verticalMenus"
        class="vertical-menus"
        :logged-user="loggedUser"
        :context-menu-prop="contextMenu"
        :menu-expanded="menuExpanded"
        :current-screen-name="currentScreenName"
        @main-menu-expand="expandMenu"
        @context-menu-expand="expandMenu"
        @show-sub-menu="showHideSubMenu"
    />
    <div :class="['App-content', !isLoggedView ? 'logged-out' : '']">
      <div class="main">
        <ProfileModale
            v-if="profileId"
            :user-id="profileId"
            :open="openProfileModale"
            @close-modale="openProfileModale = false"
        />
        <router-view/>
      </div>
      <AppFooter class="App-footer"/>
    </div>
  </div>
  <MatomoView/>
</template>

<script lang="ts">
import {defineComponent} from "vue"
import ProfileModale from "@/pages/profile/ProfileModale.vue"
import MenuView from "@/views/menus/MenuView.vue"
import MatomoView from "@/views/utils/MatomoView.vue"
import VerticalMenus from "@/views/menus/VerticalMenus.vue"
import {ContextMenu} from "@/views/menus/MenuItems"
import AppFooter from "@/views/commons/AppFooter.vue"
import Events from "@/services/Events"
import {eventBus} from "@/services/EventBus"
import {LoginService} from "@/services/rest/login/LoginService"
import {InfoReportingService} from "@/services/log/InfoReportingService"
import {UtilisateurDTO} from "@/model/bean/GeneratedDTOs"

export default defineComponent({
  name: "App",
  components: {
    MenuView,
    MatomoView,
    VerticalMenus,
    ProfileModale,
    AppFooter,
  },
  data() {
    return {
      loginService: LoginService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,

      loggedUser: new UtilisateurDTO() as UtilisateurDTO | undefined,
      showSubMenu: false,
      menuExpanded: true,
      mainTitle: "",
      subTitle: "",
      subTitleIcon: "",
      displaySubTitleInGreen: false,
      subTitleIsEditable: false,
      currentScreenName: "",
      contextMenu: new ContextMenu([], []) as ContextMenu,
      profileId: "",
      openProfileModale: false,
    }
  },
  computed: {
    isLoggedView(): boolean {
      // Ne pas afficher la vue connectée sur les pages d'authentification
      const publicRoutes = [
        "/login",
        "/register",
        "/forgot-password",
        "/reset-password",
        "/register-pending",
      ]

      const isPublicRoute = publicRoutes.some((route) => this.$route.path.startsWith(route))

      // Retourner false si on est sur une route publique, même si l'utilisateur est connecté
      if (isPublicRoute) {
        return false
      }

      return !!(this.loggedUser && this.loggedUser.id)
    },
  },
  created(): void {
    // Initialize default screen name
    this.currentScreenName = this.$t("back-to-list")

    // From PageView
    eventBus.on("main-title-changed", (title: string) => {
      this.mainTitle = title
    })
    eventBus.on("sub-title-changed", (subTitle: string) => {
      this.subTitle = subTitle
      // Two scenarios for current screen :
      // If subtitle is not empty, then this is the current screen name
      if (this.subTitle != "") {
        this.currentScreenName = this.subTitle
      } else {
        // Otherwise, we are in a noSubTitleExpected situation (see PageView)
        // Here let's use the mainTitle as screenName
        this.currentScreenName = this.mainTitle
      }
    })
    eventBus.on(
        "sub-title-icon-changed",
        (subTitleIcon: string) => (this.subTitleIcon = subTitleIcon),
    )
    eventBus.on(
        "sub-title-is-editable-changed",
        (subTitleIsEditable: boolean) => (this.subTitleIsEditable = subTitleIsEditable),
    )
    eventBus.on(
        "display-sub-title-in-green-changed",
        (displaySubTitleInGreen: boolean) => (this.displaySubTitleInGreen = displaySubTitleInGreen),
    )
    eventBus.on("contextual-menu-changed", (contextMenu: ContextMenu) => {
      // Use nextTick to avoid recursive updates in RouterLink components
      this.$nextTick(() => {
        this.contextMenu = contextMenu
      })
    })
    eventBus.on("contextual-menu-anchor-update", (newAnchor: string) => {
      this.contextMenu.activeAnchor = newAnchor
    })
    eventBus.on("open-profile-modale", (data: { show: boolean; id?: string }) => {
      if (data.id) {
        this.profileId = data.id
      } else {
        this.profileId = this.loggedUser && this.loggedUser.id ? this.loggedUser.id.toString() : ""
      }
      this.openProfileModale = data.show
    })
  },
  mounted(): void {
    this.loginService.on(Events.LOGIN_STATUS_CHANGED, () => {
      this.refreshUserInfo()
    })
  },
  methods: {
    refreshUserInfo(): void {
      this.loggedUser = this.loginService.getLoggedUser()
      if (!this.isLoggedView && "/login" !== this.$route.path) {
        this.$router.push("/login")
        this.mainTitle = ""
        this.subTitle = ""
        this.subTitleIcon = ""
      }

      const menuView = this.$refs["menuView"] as InstanceType<typeof MenuView>
      const verticalMenus = this.$refs["verticalMenus"] as InstanceType<typeof VerticalMenus>

      menuView.hideDropDown()
      verticalMenus.refreshMenuForNewLoggedUser(this.loggedUser)
    },

    logout(): void {
      this.loginService.logout().then(() => {
        this.infoReportingService.messageType(this.$t("logged-out").toString(), "is-danger")
        this.$router.push("/login")
      })
    },

    expandMenu(expand: boolean): void {
      this.menuExpanded = expand
      this.$nextTick(() => {
        document.getElementById("menu")?.dispatchEvent(new Event("menu-expand"))
      })
    },

    showHideSubMenu(show: boolean): void {
      this.showSubMenu = show
      this.menuExpanded = true
      if (!show) {
        this.contextMenu.items = []
      }
    },

    openProfile(userId: string): void {
      this.profileId = userId
      this.openProfileModale = true
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

#app {
  font-family: $font-family;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: $text-default-color;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

#appViewer {
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: auto 1fr;
  width: 100%;
  height: 100%;
  gap: 0;
}

html {
  scroll-behavior: smooth;
  overflow-y: auto !important;
}

body {
  height: 100vh;
  margin: 0;
  overflow: hidden;
}

#menu {
  grid-row: 1;
  grid-column: 1 / -1;
}

.vertical-menus {
  grid-row: 2;
  grid-column: 1;
  overflow-y: auto;
}

.App-content {
  grid-row: 2;
  grid-column: 2;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;

  &.logged-out {
    grid-column: 1 / -1;
  }
}

.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-y: auto;
  position: relative;

  router-view {
    flex: 1;
    overflow-y: auto;
    width: 100%;
  }
}

.App-footer {
  flex-shrink: 0;
  width: 100%;
}
</style>
